import { useLazyQuery } from '@apollo/client/react';
import { useEffect, useMemo, useState } from 'react';
import { Checkbox } from '@mui/material';
import { generateColumnsDefaultConfiguration } from '../../../../components/dataGrid/columnHelpers';
import { GET_ROLES } from '../../../../data/graphQL/queries/AdminRoles/getRoles';
import { GET_ROLES_NO_COMPATIBLES } from '../../../../data/graphQL/queries/AdminRoles/getRolesNoCompatibles';
import { IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';

export default function useRoleIncompatibleRoles({ roleId, onError }) {
  const [catalogRows, setCatalogRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [draftRows, setDraftRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [getRoles, { data: dataRoles, error: errorRoles, loading: loadingRoles }] = useLazyQuery(GET_ROLES, {
    fetchPolicy: 'cache-and-network'
  });

  const [getRolNoCompatible, { data: dataRolNoCompatible, error: errorRolNoCompatible, loading: loadingRolNoCompatible }] = useLazyQuery(GET_ROLES_NO_COMPATIBLES, {
    fetchPolicy: 'cache-and-network'
  });

  useEffect(() => {
    getRoles();
  }, []);

  useEffect(() => {
    if (!dataRoles?.roles?.success) return;
    const roles = dataRoles.roles.valorEsperado || [];
    setCatalogRows(roleId > 0 ? roles.filter((row) => row.id !== roleId) : roles);
  }, [dataRoles, roleId]);

  useEffect(() => {
    if (errorRoles) onError?.('ERRORDB');
  }, [errorRoles, onError]);

  useEffect(() => {
    if (!roleId || roleId <= 0 || catalogRows.length === 0) return;
    getRolNoCompatible({ variables: { idRol: roleId } });
  }, [roleId, catalogRows, getRolNoCompatible]);

  useEffect(() => {
    if (!dataRolNoCompatible?.rolNoCompatible?.success) return;
    const rows = dataRolNoCompatible.rolNoCompatible.valorEsperado || [];
    setSelectedRows(rows);
    setDraftRows(rows);
  }, [dataRolNoCompatible]);

  useEffect(() => {
    if (errorRolNoCompatible) onError?.('ERRORDB');
  }, [errorRolNoCompatible, onError]);

  const selectedIds = useMemo(
    () => new Set(selectedRows.map((row) => row.idRolNoCompatible)),
    [selectedRows]
  );

  const availableRows = useMemo(
    () => catalogRows.filter((row) => !selectedIds.has(row.id)),
    [catalogRows, selectedIds]
  );

  const getDraftById = (id) => draftRows.find((row) => row.idRolNoCompatible === id);

  const toggleRole = (row) => {
    const item = getDraftById(row.id);
    if (item) {
      setDraftRows((current) => current.filter((entry) => entry.idRolNoCompatible !== row.id));
      return;
    }
    setDraftRows((current) => [...current, { idRolNoCompatible: row.id }]);
  };

  const removeSelected = (row) => {
    const filtered = selectedRows.filter((item) => item.idRolNoCompatible !== row.idRolNoCompatible);
    setSelectedRows(filtered);
    setDraftRows(filtered);
  };

  const openDialog = () => {
    setDraftRows(selectedRows);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDraftRows(selectedRows);
    setDialogOpen(false);
  };

  const confirmDialog = () => {
    setSelectedRows(draftRows);
    setDialogOpen(false);
  };

  const selectedColumns = useMemo(() => generateColumnsDefaultConfiguration([
    {
      field: 'descripcion',
      headerName: 'Descripcion',
      valueGetter: (_, row) => catalogRows.find((entry) => entry.id === row.idRolNoCompatible)?.descripcion || ''
    },
    {
      field: 'accionesRol',
      headerName: 'Acciones',
      flex: 0.1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title='Eliminar'>
          <IconButton onClick={() => removeSelected(params.row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      )
    }
  ]), [selectedRows, catalogRows]);

  const availableColumns = useMemo(() => generateColumnsDefaultConfiguration([
    {
      field: 'seleccionar',
      headerName: 'Seleccionar',
      flex: 0.08,
      sortable: false,
      renderCell: (params) => (
        <Checkbox
          checked={Boolean(getDraftById(params.row.id))}
          onChange={() => toggleRole(params.row)}
        />
      )
    },
    { field: 'descripcion', headerName: 'Descripcion', flex: 0.4 }
  ]), [draftRows]);

  return {
    selectedRows,
    availableRows,
    dialogOpen,
    selectedColumns,
    availableColumns,
    openDialog,
    closeDialog,
    confirmDialog,
    removeSelected,
    loading: loadingRoles || loadingRolNoCompatible,
    error: errorRoles || errorRolNoCompatible
  };
}
